/**
 * Image Cropping Utility
 * Generates cropped thumbnails from original image data based on crop settings
 */

/**
 * Calculate optimal center crop for a given print ratio and orientation
 * @param {number} imageWidth - Original image width
 * @param {number} imageHeight - Original image height
 * @param {number} ratioX - Print ratio X (e.g., 6 for 6x4)
 * @param {number} ratioY - Print ratio Y (e.g., 4 for 6x4)
 * @param {string} orientation - 'landscape' or 'portrait'
 * @returns {object} Crop settings as percentages {x, y, width, height}
 */
const calculateOptimalCenterCrop = (imageWidth, imageHeight, ratioX, ratioY, orientation) => {
  // Determine target aspect ratio based on orientation
  const isNaturallyLandscape = ratioX > ratioY;
  let targetRatio;
  
  if (orientation === 'portrait') {
    // Portrait: taller than wide
    targetRatio = isNaturallyLandscape
      ? Math.min(ratioX, ratioY) / Math.max(ratioX, ratioY)  // 6x4 portrait: 4/6 = 0.667
      : Math.max(ratioX, ratioY) / Math.min(ratioX, ratioY); // 8x10 portrait: 10/8 = 1.25... wait that's wrong
  } else {
    // Landscape: wider than tall
    targetRatio = isNaturallyLandscape
      ? Math.max(ratioX, ratioY) / Math.min(ratioX, ratioY)  // 6x4 landscape: 6/4 = 1.5
      : Math.min(ratioX, ratioY) / Math.max(ratioX, ratioY); // 8x10 landscape: 8/10 = 0.8
  }
  
  // For portrait, we want height > width, so ratio < 1
  // For landscape, we want width > height, so ratio > 1
  if (orientation === 'portrait') {
    targetRatio = Math.min(ratioX, ratioY) / Math.max(ratioX, ratioY);
  } else {
    targetRatio = Math.max(ratioX, ratioY) / Math.min(ratioX, ratioY);
  }
  
  const imageRatio = imageWidth / imageHeight;
  
  let cropWidth, cropHeight, cropX, cropY;
  
  if (imageRatio > targetRatio) {
    // Image is wider than target - crop the width, use full height
    cropHeight = imageHeight;
    cropWidth = cropHeight * targetRatio;
    cropX = (imageWidth - cropWidth) / 2;  // Center horizontally
    cropY = 0;
  } else {
    // Image is taller than target - crop the height, use full width
    cropWidth = imageWidth;
    cropHeight = cropWidth / targetRatio;
    cropX = 0;
    cropY = (imageHeight - cropHeight) / 2;  // Center vertically
  }
  
  // Convert to percentages
  const result = {
    x: (cropX / imageWidth) * 100,
    y: (cropY / imageHeight) * 100,
    width: (cropWidth / imageWidth) * 100,
    height: (cropHeight / imageHeight) * 100
  };
  
  console.log(`🎯 Auto-calculated optimal crop for ${ratioX}x${ratioY} ${orientation}:`, {
    targetRatio: targetRatio.toFixed(3),
    imageRatio: imageRatio.toFixed(3),
    crop: result
  });
  
  return result;
};

/**
 * Check if crop settings are "default" (full image, no actual crop)
 */
const isDefaultCrop = (cropSettings) => {
  return cropSettings.x === 0 && 
         cropSettings.y === 0 && 
         cropSettings.width === 100 && 
         cropSettings.height === 100;
};

/**
 * Create a cropped image from base64 data
 * @param {string} imageSrc - Base64 image data (with or without data:image prefix)
 * @param {object} cropSettings - Crop settings in percentages {x, y, width, height}
 * @param {number} rotation - Rotation in degrees (deprecated, kept for compatibility)
 * @param {number} targetWidth - Target width for the thumbnail (default 400px)
 * @param {object} printRatio - Print aspect ratio {ratioX, ratioY, orientation} for thumbnail dimensions
 * @param {number} zoom - Zoom level from react-easy-crop (default 1)
 * @returns {Promise<string>} Base64 string of cropped image (without data:image prefix)
 */
export const generateCroppedThumbnail = (imageSrc, cropSettings, rotation = 0, targetWidth = 400, printRatio = null, zoom = 1) => {
  return new Promise((resolve, reject) => {
    // Ensure imageSrc has data:image prefix
    const fullImageSrc = imageSrc.startsWith('data:') 
      ? imageSrc 
      : `data:image/jpeg;base64,${imageSrc}`;
    
    const image = new Image();
    image.crossOrigin = 'anonymous';
    
    image.onload = () => {
      try {
        // Get original image dimensions
        const originalWidth = image.naturalWidth || image.width;
        const originalHeight = image.naturalHeight || image.height;
        
        console.log(`🖼️ Original image dimensions: ${originalWidth}x${originalHeight}`);
        
        // AUTO-CALCULATE optimal crop if using default settings and we have print ratio
        let finalCropSettings = cropSettings;
        if (isDefaultCrop(cropSettings) && printRatio && printRatio.ratioX && printRatio.ratioY) {
          console.log(`📐 Default crop detected - auto-calculating optimal crop for ${printRatio.ratioX}x${printRatio.ratioY} ${printRatio.orientation}`);
          finalCropSettings = calculateOptimalCenterCrop(
            originalWidth, 
            originalHeight, 
            printRatio.ratioX, 
            printRatio.ratioY, 
            printRatio.orientation || 'landscape'
          );
        }
        
        console.log(`📐 Crop settings (percentages):`, finalCropSettings);
        
        // Calculate crop dimensions in pixels from percentages
        const cropX = (finalCropSettings.x / 100) * originalWidth;
        const cropY = (finalCropSettings.y / 100) * originalHeight;
        const cropWidth = (finalCropSettings.width / 100) * originalWidth;
        const cropHeight = (finalCropSettings.height / 100) * originalHeight;
        
        console.log(`✂️ Crop pixels: ${Math.round(cropWidth)}x${Math.round(cropHeight)} at (${Math.round(cropX)}, ${Math.round(cropY)})`);
        
        // STEP 1: Create a temporary canvas to extract the cropped area
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        tempCanvas.width = cropWidth;
        tempCanvas.height = cropHeight;
        
        // Draw just the cropped area to the temp canvas
        tempCtx.drawImage(
          image,
          cropX, cropY, cropWidth, cropHeight,  // Source crop area
          0, 0, cropWidth, cropHeight            // Fill temp canvas
        );
        
        // STEP 2: Calculate base thumbnail dimensions based on print ratio and orientation
        let baseWidth, baseHeight;
        
        if (printRatio && printRatio.ratioX && printRatio.ratioY) {
          // Determine if naturally landscape or portrait
          // 6x4 (ratioX=6, ratioY=4) is naturally landscape (6 > 4)
          // 8x10 (ratioX=8, ratioY=10) is naturally portrait (8 < 10)
          const isNaturallyLandscape = printRatio.ratioX > printRatio.ratioY;
          
          if (printRatio.orientation === 'portrait') {
            // Portrait: HEIGHT > WIDTH (taller)
            const aspectRatio = isNaturallyLandscape
              ? printRatio.ratioX / printRatio.ratioY  // 6x4 portrait: 6/4 = 1.5
              : printRatio.ratioY / printRatio.ratioX; // 8x10 portrait: 10/8 = 1.25
            baseWidth = targetWidth;
            baseHeight = Math.round(targetWidth * aspectRatio);
          } else {
            // Landscape: WIDTH > HEIGHT (wider)
            const aspectRatio = isNaturallyLandscape
              ? printRatio.ratioY / printRatio.ratioX  // 6x4 landscape: 4/6 = 0.667
              : printRatio.ratioX / printRatio.ratioY; // 8x10 landscape: 8/10 = 0.8
            baseWidth = targetWidth;
            baseHeight = Math.round(targetWidth * aspectRatio);
          }
          
          console.log(`📐 Print ratio: ${printRatio.ratioX}x${printRatio.ratioY} (naturally ${isNaturallyLandscape ? 'landscape' : 'portrait'}) ${printRatio.orientation} → base=${baseWidth}x${baseHeight}`);
        } else {
          // Fallback: use crop area aspect ratio
          const cropAspectRatio = cropWidth / cropHeight;
          baseWidth = targetWidth;
          baseHeight = Math.round(targetWidth / cropAspectRatio);
        }
        
        // Verify aspect ratios match
        const cropAspect = (cropWidth / cropHeight).toFixed(3);
        const baseAspect = (baseWidth / baseHeight).toFixed(3);
        console.log(`🔍 Crop aspect: ${cropAspect}, Base aspect: ${baseAspect}, Crop size: ${Math.round(cropWidth)}x${Math.round(cropHeight)}`);
        
        // STEP 3: Determine final canvas dimensions
        // SIMPLIFIED: Don't swap dimensions for rotation - generate thumbnail in natural orientation
        // CSS rotation will be applied when displaying the thumbnail
        const canvasWidth = baseWidth;
        const canvasHeight = baseHeight;
        
        console.log(`📐 Final canvas: ${canvasWidth}x${canvasHeight} (rotation=${rotation}° will be applied via CSS)`);
        
        // STEP 4: Create final canvas WITHOUT rotation
        // Rotation is handled via CSS when displaying the thumbnail
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        
        console.log(`🎨 Creating canvas: ${canvasWidth}x${canvasHeight}, tempCanvas: ${tempCanvas.width}x${tempCanvas.height}`);
        
        // Simple draw - no rotation applied to canvas
        // The rotation value is stored with the thumbnail and applied via CSS when displayed
        ctx.drawImage(
          tempCanvas,
          0, 0,
          canvasWidth, canvasHeight
        );
        
        // Convert to base64 (JPEG with 85% quality for smaller file size)
        const base64Data = canvas.toDataURL('image/jpeg', 0.85);
        
        // Remove the data:image/jpeg;base64, prefix to save space
        const base64WithoutPrefix = base64Data.split(',')[1];
        
        resolve(base64WithoutPrefix);
      } catch (error) {
        reject(error);
      }
    };
    
    image.onerror = () => {
      reject(new Error('Failed to load image for cropping'));
    };
    
    image.src = fullImageSrc;
  });
};

/**
 * Generate cropped thumbnails for all sizes with crop data
 * @param {string} originalImageData - Base64 image data of the original image
 * @param {object} cropsPerSize - Object with size keys and crop data {crop_settings, rotation, aspectOrientation}
 * @returns {Promise<object>} Object with size keys and cropped thumbnail base64 strings
 */
export const generateAllCroppedThumbnails = async (originalImageData, cropsPerSize) => {
  const croppedThumbnails = {};
  
  for (const [size, cropData] of Object.entries(cropsPerSize)) {
    if (cropData && cropData.crop_settings) {
      try {
        const croppedThumb = await generateCroppedThumbnail(
          originalImageData,
          cropData.crop_settings,
          cropData.rotation || 0,
          400, // 400px width for thumbnails
          null,
          cropData.zoom || 1
        );
        croppedThumbnails[size] = croppedThumb;
        console.log(`✅ Generated cropped thumbnail for size ${size}`);
      } catch (error) {
        console.error(`Failed to generate thumbnail for size ${size}:`, error);
      }
    }
  }
  
  return croppedThumbnails;
};

/**
 * Generate a single cropped thumbnail for a specific size
 * @param {string} originalImageData - Base64 image data of the original image
 * @param {string} size - Print size key (e.g., "6x4")
 * @param {object} cropData - Crop data {crop_settings, rotation, printRatio, zoom}
 * @returns {Promise<string>} Base64 string of cropped thumbnail
 */
export const generateSingleCroppedThumbnail = async (originalImageData, size, cropData) => {
  if (!cropData || !cropData.crop_settings) {
    throw new Error('No crop settings provided');
  }
  
  return generateCroppedThumbnail(
    originalImageData,
    cropData.crop_settings,
    cropData.rotation || 0,
    400,
    cropData.printRatio || null,  // Pass print ratio for correct thumbnail dimensions
    cropData.zoom || 1  // Pass zoom level
  );
};
